// A minimal typed pub-sub. Not using EventTarget/CustomEvent so this
// works identically in SSR/Node/tests without a DOM.
class EventsBus {
  constructor() {
    this.listeners = new Map(); // eventName -> Set<callback>
  }

  on(eventName, callback) {
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, new Set());
    }
    this.listeners.get(eventName).add(callback);
    return () => this.off(eventName, callback); // unsubscribe convenience
  }

  // Subscribe for exactly one emission, then auto-unsubscribe.
  // Useful for "wait for the next X" without manually managing cleanup.
  once(eventName, callback) {
    const unsubscribe = this.on(eventName, (payload) => {
      unsubscribe();
      callback(payload);
    });
    return unsubscribe;
  }

  off(eventName, callback) {
    this.listeners.get(eventName)?.delete(callback);
  }

  // Remove all listeners for one event, or everything if no name is given.
  // Handy in tests (afterEach(() => eventsBus.clear())) so listeners from
  // one test don't leak into the next.
  clear(eventName) {
    if (eventName) {
      this.listeners.delete(eventName);
    } else {
      this.listeners.clear();
    }
  }

  // Lets a caller check "is anyone even listening?" before doing
  // expensive work to build a payload, or in tests to assert
  // a listener was registered/cleaned up correctly.
  listenerCount(eventName) {
    return this.listeners.get(eventName)?.size ?? 0;
  }

  emit(eventName, payload) {
    this.listeners.get(eventName)?.forEach((cb) => {
      try {
        cb(payload);
      } catch (err) {
        // one bad listener shouldn't break others, or the interceptor itself
        console.error(`eventsBus listener for "${eventName}" threw:`, err);
      }
    });
  }
}

export const eventsBus = new EventsBus();

// Centralize event names — avoids typos scattered across files,
// and gives you one place to see everything the API layer can emit.
export const API_EVENTS = {
  CONFLICT: 'api:conflict',
  LOGGED_OUT: 'api:loggedOut',
  // room to grow: NETWORK_ERROR, RATE_LIMITED, etc.
};
